import { useId, type ReactNode } from 'react';
import { Icon } from '../atoms/Icon';
import { Label } from '../atoms/Label';
import { Text } from '../atoms/Text';
import styles from './FormField.module.css';

/** Props to spread onto the control so label, hint and error are connected. */
export interface FieldControlProps {
  id: string;
  'aria-describedby'?: string;
  'aria-invalid'?: true;
  invalid: boolean;
  required?: boolean;
}

export interface FormFieldProps {
  label: string;
  /** Help text below the control. */
  hint?: ReactNode;
  /** Error message; marks the control invalid and is announced. */
  error?: string | undefined;
  required?: boolean;
  /** Content on the right of the label, e.g. a "Passwort vergessen?" link. */
  labelAside?: ReactNode;
  children: (control: FieldControlProps) => ReactNode;
}

/** Label + control + hint + error with correct ARIA wiring (DS-004). */
export function FormField({
  label,
  hint,
  error,
  required = false,
  labelAside,
  children,
}: FormFieldProps) {
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const describedBy = [hint ? hintId : undefined, error ? errorId : undefined]
    .filter(Boolean)
    .join(' ');
  return (
    <div className={styles.field}>
      <div className={styles.labelRow}>
        <Label htmlFor={id} required={required}>
          {label}
        </Label>
        {labelAside && <span className={styles.aside}>{labelAside}</span>}
      </div>
      {children({
        id,
        invalid: Boolean(error),
        ...(describedBy ? { 'aria-describedby': describedBy } : {}),
        ...(error ? { 'aria-invalid': true as const } : {}),
        ...(required ? { required: true } : {}),
      })}
      {hint && (
        <Text as="span" size="sm" tone="muted" id={hintId} className={styles.hint}>
          {hint}
        </Text>
      )}
      {error && (
        <Text as="span" size="sm" tone="danger" id={errorId} role="alert" className={styles.error}>
          <Icon name="alert-circle" size="xs" className={styles.errorIcon} />
          {error}
        </Text>
      )}
    </div>
  );
}
